// src/components/Header/Header.jsx
import React, { useState, useRef, useEffect, useContext } from "react";
import { useLanguage } from "../../LanguageProvider";
import logo from "../../assets/logo.png";
import { FaChevronDown, FaUser } from "react-icons/fa";
import "./Header.css";
import SavedAddressesModal from "../SavedAddressesModal/SavedAddressesModal";
import NewAddressModal from "../NewAddressModal/NewAddressModal";
import { MallContext } from "../../MallContext";
import Services from "../../services/Services";
import { generateRandomCaseSensitiveString } from "../../utils/generateRandomCaseSensitiveString";

const flags = {
  tr: "https://upload.wikimedia.org/wikipedia/commons/b/b4/Flag_of_Turkey.svg",
  en: "https://upload.wikimedia.org/wikipedia/en/a/ae/Flag_of_the_United_Kingdom.svg"
};

const Header = () => {
  const { language, changeLanguage, getTranslation } = useLanguage();
  const { token } = useContext(MallContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isNewAddressModalOpen, setIsNewAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const rightSectionRef = useRef(null);

  // Kayıtlı adresler için mock + API override
  const [savedAddresses, setSavedAddresses] = useState([]);

  // ✅ Kayıtlı adresleri backend'den çek
  useEffect(() => {
    if (!token) return;
    const customerID = localStorage.getItem("customerID");
    if (!customerID) return;

    Services.GetUsersBillingAddresses(token, customerID)
      .then((res) => {
        if (!res.data || !res.data.status || !Array.isArray(res.data.result)) {
          return;
        }

        const mapped = res.data.result.map((item) => ({
          id: item.id || generateRandomCaseSensitiveString(10),
          label: item.title || "Adres",
          detail: item.address || "(Adres detayı)",
          city: item.city || "",
          district: item.neighbour || item.region || "",
          postalCode: item.postalCode || "",
          address: item.street || item.address || "",
          phone: item.phone || "",
          email: item.email || ""
        }));

        setSavedAddresses(mapped);
      })
      .catch((err) => {
      });
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        rightSectionRef.current &&
        !rightSectionRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setDropdownOpen((open) => !open);
    setUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen((open) => !open);
    setDropdownOpen(false);
  };

  const selectLanguage = (lang) => {
    changeLanguage(lang);
    setDropdownOpen(false);
  };

  const handleAddressClick = () => {
    setIsAddressModalOpen(true);
    setUserMenuOpen(false);
  };

  const handleAddressSelect = (addr) => {
    setEditingAddress({
      city: addr.city || "",
      district: addr.district || "",
      postalCode: addr.postalCode || "",
      address: addr.address || "",
      phone: addr.phone || "",
      email: addr.email || ""
    });
    setIsAddressModalOpen(false);
    setIsNewAddressModalOpen(true);
  };

  const handleAddNewAddress = () => {
    setEditingAddress(null);
    setIsAddressModalOpen(false);
    setIsNewAddressModalOpen(true);
  };

  const handleAddressSave = async (values) => {
    // values: { city, district, postalCode, address, phone, email }

    const customerID = localStorage.getItem("customerID") || "-";
    const id = generateRandomCaseSensitiveString(20);

    const storedPhone = localStorage.getItem("phoneNumber");
    const storedEmail = localStorage.getItem("email");

    const phone = values.phone || storedPhone || "-";
    const email = values.email || storedEmail || "-";
    const city = values.city || "-";
    const district = values.district || "-";
    const postalCode = values.postalCode || "";
    const address = values.address || "-";
    const country = "Türkiye";

    let label = values.label;
    const street = address;
    const neighbour = district;
    const region = city;
    const cityVal = city;
    const countryVal = country;
    const fullAddress = `${address} ${city}/${district} ${postalCode ? `(${postalCode})` : ""
      }`.trim();

    try {
      await Services.AddUserBillingAddress(
        token,
        id,
        customerID,
        label,
        street,
        neighbour,
        region,
        cityVal,
        countryVal,
        phone,
        fullAddress,
        "11111111111",
        email,
        "-"
      );
    } catch (err) {
    }

    if (!editingAddress) {
      setSavedAddresses((prev) => [
        ...prev,
        {
          id,
          label,
          detail: fullAddress,
          city,
          district,
          postalCode,
          address,
          phone,
          email
        }
      ]);
    } else {
      setSavedAddresses((prev) =>
        prev.map((addr) =>
          addr.city === editingAddress.city &&
            addr.district === editingAddress.district &&
            addr.address === editingAddress.address
            ? {
              ...addr,
              label,
              city,
              district,
              postalCode,
              address,
              phone,
              email,
              detail: fullAddress
            }
            : addr
        )
      );
    }

    setIsNewAddressModalOpen(false);
    setEditingAddress(null);
  };

  return (
    <>
      <header className="header">
        <div className="logo">
          <img src={logo} alt="logo" />
        </div>

        <div className="rightSection" ref={rightSectionRef}>
          {/* Dil seçimi */}
          <div className="dropdownContainer">
            <button onClick={toggleDropdown} className="dropdownButton">
              <img src={flags[language]} alt={language} className="flagIcon" />
              <FaChevronDown style={{ marginLeft: 8, marginBottom: 3 }} />
            </button>
            {dropdownOpen && (
              <div className="dropdownMenu">
                <div
                  className="dropdownItem"
                  onClick={() => selectLanguage("tr")}
                >
                  <img src={flags.tr} alt="tr" className="flagIcon" /> Türkçe
                </div>
                <div
                  className="dropdownItem"
                  onClick={() => selectLanguage("en")}
                >
                  <img src={flags.en} alt="en" className="flagIcon" /> English
                </div>
              </div>
            )}
          </div>

          {/* Kullanıcı avatar + menü */}
          <div className="userMenuContainer">
            <button className="avatarContainer" onClick={toggleUserMenu}>
              <FaUser className="avatarIcon" />
            </button>
            {userMenuOpen && (
              <div className="userMenu">
                <div className="userMenuItem" onClick={handleAddressClick}>
                  {getTranslation("myAddressInformations")}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Kayıtlı adresler listesi */}
      <SavedAddressesModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        addresses={savedAddresses}
        onSelect={handleAddressSelect}
        onAddNew={handleAddNewAddress}
      />

      {/* Yeni / mevcut adres detay modalı */}
      <NewAddressModal
        isOpen={isNewAddressModalOpen}
        onClose={() => {
          setIsNewAddressModalOpen(false);
          setEditingAddress(null);
        }}
        onSave={handleAddressSave}
        initialValues={editingAddress}
      />
    </>
  );
};

export default Header;